import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { requestService } from '../../services/requestService';
import { dealService } from '../../services/dealService';
import { ServiceRequest, Deal, UserRole } from '../../types';
import { useAuthStore } from '../../store/authStore';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

type MapItem = {
  id: string;
  title: string;
  subtitle: string;
  latitude: number;
  longitude: number;
  path?: string;
  kind: 'request' | 'deal';
};

const ZW_CENTER: [number, number] = [-19.0154, 29.1549];

const roleBase = (role?: UserRole) => {
  if (role === UserRole.ADMIN) return '/admin';
  if (role === UserRole.SURVEYOR) return '/surveyor';
  return '/client';
};

const requestPath = (role: UserRole | undefined, id: string) => {
  if (role === UserRole.ADMIN) return `/admin/requests/${id}`;
  if (role === UserRole.SURVEYOR) return `/surveyor/request/${id}`;
  return `/client/request/${id}`;
};

export const PortalMapScreen: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [requestData, dealData] = await Promise.all([
          requestService.list({ page: 1 }),
          dealService.list({ page: 1 }).catch(() => ({ results: [] as Deal[] })),
        ]);
        if (!mounted) return;
        setRequests(requestData.results ?? []);
        setDeals(dealData.results ?? []);
      } catch {
        if (mounted) setError('Failed to load map locations.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user?.role]);

  const items = useMemo<MapItem[]>(() => {
    const requestItems = requests
      .filter((request) => request.latitude != null && request.longitude != null)
      .map((request) => ({
        id: request.id,
        title: request.service_type || 'Survey request',
        subtitle: `${request.status} - ${request.address || 'No address'}`,
        latitude: Number(request.latitude),
        longitude: Number(request.longitude),
        path: requestPath(user?.role, request.id),
        kind: 'request' as const,
      }));
    const dealItems = deals
      .filter((deal) => deal.request?.latitude != null && deal.request?.longitude != null)
      .map((deal) => ({
        id: deal.id,
        title: deal.request?.service_type || 'Active deal',
        subtitle: `${deal.status} - $${Number(deal.deal_value || deal.agreed_price || 0).toFixed(2)}`,
        latitude: Number(deal.request.latitude),
        longitude: Number(deal.request.longitude),
        path: user?.role === UserRole.ADMIN ? '/admin/deals' : `${roleBase(user?.role)}/deal/${deal.id}`,
        kind: 'deal' as const,
      }));
    return [...dealItems, ...requestItems];
  }, [requests, deals, user?.role]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapRef.current = L.map(containerRef.current, {
      center: ZW_CENTER,
      zoom: 6,
      maxBounds: [[-22.5, 25.2], [-15.3, 33.1]],
      maxBoundsViscosity: 0.8,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(mapRef.current);
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    items.forEach((item) => {
      const marker = L.marker([item.latitude, item.longitude]).addTo(mapRef.current!);
      marker.bindPopup(`<strong>${item.title}</strong><br/>${item.subtitle}`);
      marker.on('click', () => item.path && navigate(item.path));
      markersRef.current.push(marker);
    });
    if (items.length > 0) {
      const bounds = L.latLngBounds(items.map((item) => [item.latitude, item.longitude] as [number, number]));
      mapRef.current.fitBounds(bounds, { padding: [36, 36], maxZoom: 13 });
    }
  }, [items, navigate]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Map</h1>
          <p style={styles.subtitle}>Live request and job locations across your portal.</p>
        </div>
        <div style={styles.count}>{items.length} locations</div>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.mapWrap}>
        <div ref={containerRef} style={styles.map} />
        {loading && <div style={styles.loading}>Loading locations...</div>}
      </div>
      <div style={styles.list}>
        {items.map((item) => (
          <button key={`${item.kind}-${item.id}`} type="button" style={styles.item} onClick={() => item.path && navigate(item.path)}>
            <span style={styles.itemKind}>{item.kind}</span>
            <span style={styles.itemBody}>
              <strong>{item.title}</strong>
              <small>{item.subtitle}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 14 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: 'var(--sc-text-secondary)' },
  count: { border: '1px solid var(--sc-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 800, color: 'var(--sc-brand-primary)', background: 'var(--sc-bg-secondary)' },
  error: { border: '1px solid var(--sc-error)', borderRadius: 8, padding: 10, background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', fontSize: 13, fontWeight: 700 },
  mapWrap: { height: 'min(62vh, 560px)', minHeight: 360, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--sc-border)', position: 'relative' },
  map: { width: '100%', height: '100%' },
  loading: { position: 'absolute', top: 12, left: 12, padding: '7px 10px', borderRadius: 8, background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', color: 'var(--sc-text-secondary)', fontSize: 12, fontWeight: 700 },
  list: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 },
  item: { display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', border: '1px solid var(--sc-border)', borderRadius: 8, padding: 12, background: 'var(--sc-bg-secondary)', cursor: 'pointer' },
  itemKind: { textTransform: 'uppercase', fontSize: 10, fontWeight: 800, color: 'var(--sc-brand-primary)', background: 'var(--sc-bg-active)', borderRadius: 6, padding: '4px 6px' },
  itemBody: { display: 'flex', flexDirection: 'column', gap: 3, color: 'var(--sc-text-primary)', minWidth: 0 },
};
